'use client';

import { useEffect } from 'react';
import Script from 'next/script';

/**
 * Shopify Cart Tracking Component
 * Initializes Shopify Buy Button SDK and tracks cart events via DOM monitoring
 *
 * Note: Modern Shopify SDK doesn't expose cart.model.on() API anymore.
 * Instead, we monitor DOM changes and track events through our components.
 */
export default function ShopifyCartTracking() {
  useEffect(() => {
    // Monitor for Shopify cart changes via DOM
    const observeShopifyCart = () => {
      const observer = new MutationObserver(() => {
        const checkoutButton = document.querySelector(
          '.shopify-buy__btn[data-element="cart.checkout"]'
        );

        if (checkoutButton && !checkoutButton.hasAttribute('data-tracked')) {
          checkoutButton.setAttribute('data-tracked', 'true');

          checkoutButton.addEventListener('click', () => {
            // Track begin checkout event
            if (window.dataLayer) {
              // Get cart total from Shopify cart UI
              const cartTotalElement = document.querySelector('.shopify-buy__cart__subtotal__text');
              const cartTotalText = cartTotalElement?.textContent || '0';
              const cartTotal = parseFloat(cartTotalText.replace(/[^0-9.]/g, '')) || 0;

              // Count items in cart
              const cartItems = document.querySelectorAll('.shopify-buy__cart-item');

              window.dataLayer.push({
                event: 'begin_checkout',
                currency: 'USD',
                value: cartTotal,
                items_count: cartItems.length,
              });

              // Log in development
              if (process.env.NODE_ENV === 'development') {
                console.log('Shopify Checkout Started:', {
                  value: cartTotal,
                  items_count: cartItems.length,
                });
              }
            }

            // Add attribution parameters to checkout URL
            const checkoutBtn = checkoutButton as HTMLAnchorElement;
            if (checkoutBtn.href && window.RCFX_SERIALIZED_URL_PARAMS) {
              const separator = checkoutBtn.href.indexOf('?') > -1 ? '&' : '?';
              checkoutBtn.href = checkoutBtn.href + separator + window.RCFX_SERIALIZED_URL_PARAMS;
            }
          });
        }

        // Track "Add to Cart" button clicks
        const addToCartButtons = document.querySelectorAll('.shopify-buy__btn[data-element="product.addToCart"]');
        addToCartButtons.forEach((button) => {
          if (!button.hasAttribute('data-tracked')) {
            button.setAttribute('data-tracked', 'true');

            button.addEventListener('click', () => {
              // Wait a bit for the product info to be available
              setTimeout(() => {
                const productComponent = button.closest('.shopify-buy__product');
                const productTitle =
                  productComponent?.querySelector('.shopify-buy__product__title')?.textContent || 'Unknown Product';
                const productPrice =
                  productComponent?.querySelector('.shopify-buy__product__actual-price')?.textContent || '0';
                const price = parseFloat(productPrice.replace(/[^0-9.]/g, '')) || 0;

                if (window.dataLayer) {
                  window.dataLayer.push({
                    event: 'add_to_cart',
                    currency: 'USD',
                    value: price,
                    items: [
                      {
                        item_name: productTitle,
                        item_brand: 'Green Filter',
                        item_category: 'Air Filters',
                        price: price,
                        quantity: 1,
                      },
                    ],
                  });

                  // Log in development
                  if (process.env.NODE_ENV === 'development') {
                    console.log('Product Added to Cart:', {
                      name: productTitle,
                      price: price,
                    });
                  }
                }
              }, 500);
            });
          }
        });
      });

      // Observe the entire document for Shopify cart changes
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return observer;
    };

    // Start observing after a short delay to ensure Shopify has loaded
    const timeoutId = setTimeout(() => {
      if (typeof window !== 'undefined') {
        const observer = observeShopifyCart();

        // Cleanup
        return () => {
          observer.disconnect();
        };
      }
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <>
      {/* Shopify Buy Button SDK */}
      <Script
        src="https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js"
        strategy="lazyOnload"
        onLoad={() => {
          if (process.env.NODE_ENV === 'development') {
            console.log('Shopify Buy Button SDK loaded');
          }
        }}
      />
    </>
  );
}
